/* eslint-disable lingui/text-restrictions */
/* eslint-disable lingui/no-unlocalized-strings */

import { t } from "@lingui/macro";
import {
  ArrowRight,
  ClipboardText,
  Clock,
  FileText,
  UserFocus,
} from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import { useAuthStore } from "@/client/stores/auth";

const capabilities = [
  {
    icon: <UserFocus weight="duotone" className="h-5 w-5" />,
    title: "Personalized From Your Resume",
    desc: "AI reads your full resume and highlights your most relevant experience for each role",
  },
  {
    icon: <FileText weight="duotone" className="h-5 w-5" />,
    title: "Matched to the Job Description",
    desc: "Each letter is tailored to the specific requirements and keywords in the posting",
  },
  {
    icon: <Clock weight="duotone" className="h-5 w-5" />,
    title: "Ready in Seconds",
    desc: "Professional 3–4 paragraph format, ready to send. What used to take an hour now takes 30 seconds",
  },
  {
    icon: <ClipboardText weight="duotone" className="h-5 w-5" />,
    title: "One-Click Copy",
    desc: "Copy the formatted letter to your clipboard instantly or regenerate for a different angle",
  },
];

export const FeatureCoverLetterSection = () => {
  const isLoggedIn = useAuthStore((s) => !!s.user);

  return (
    <section
      id="cover-letter"
      className="relative overflow-hidden bg-secondary-accent/50 py-24 sm:py-32"
    >
      <div className="container">
        <motion.div
          className="mx-auto max-w-3xl text-center"
          viewport={{ once: true }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <p className="text-sm font-semibold uppercase tracking-wider text-orange-600">
            {t`Cover Letters`}
          </p>
          <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            {t`Generate Cover Letters Matched to Every Job`}
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-foreground/60">
            No more generic cover letters. CareerBldr's AI cover letter generator reads your resume
            and the job description, then writes a personalized letter that complements your
            application in seconds, not hours.
          </p>
        </motion.div>

        {/* Input + Result screenshots side by side */}
        <motion.div
          className="mx-auto mt-12 grid max-w-5xl gap-6 sm:grid-cols-2"
          viewport={{ once: true }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0, transition: { delay: 0.1 } }}
        >
          <div>
            <span className="mb-3 inline-block text-xs font-semibold uppercase tracking-wider text-foreground/40">
              Paste your job description
            </span>
            <img
              src="/screenshots/cover-letter-input.png"
              alt="AI Cover Letter Generator input — paste a job description and company name"
              loading="lazy"
              decoding="async"
              className="w-full rounded-xl shadow-2xl ring-1 ring-foreground/10"
            />
          </div>
          <div>
            <span className="mb-3 inline-block text-xs font-semibold uppercase tracking-wider text-foreground/40">
              Get a tailored cover letter
            </span>
            <img
              src="/screenshots/cover-letter.png"
              alt="AI-generated cover letter personalized from resume and job description for Stripe"
              loading="lazy"
              decoding="async"
              className="w-full rounded-xl shadow-2xl ring-1 ring-foreground/10"
            />
          </div>
        </motion.div>

        {/* Capability cards */}
        <div className="mx-auto mt-14 grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {capabilities.map((cap, i) => (
            <motion.div
              key={cap.title}
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0, transition: { delay: 0.05 * i } }}
              className="rounded-xl border border-border/50 bg-background p-5"
            >
              <div className="text-orange-600">{cap.icon}</div>
              <h3 className="mt-3 text-sm font-semibold">{cap.title}</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-foreground/50">{cap.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="mt-12 text-center"
          viewport={{ once: true }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1, transition: { delay: 0.25 } }}
        >
          <Button asChild size="lg">
            <Link to={isLoggedIn ? "/dashboard" : "/auth/register"}>
              {isLoggedIn ? t`Generate a Cover Letter` : t`Try Cover Letter Generator`}
              <ArrowRight className="ml-2" weight="bold" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
